import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';

import { CvUploadService } from './cv-upload.service';
import { CvUploadValidationResponse } from './models/cv-upload-validation.model';

type UploadState = 'loading' | 'valid' | 'invalid' | 'uploading' | 'success' | 'error';

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

@Component({
  selector: 'app-cv-upload',
  templateUrl: './cv-upload.component.html',
  styleUrls: ['./cv-upload.component.scss'],
})
export class CvUploadComponent implements OnInit, OnDestroy {
  public state: UploadState = 'loading';
  public validation: CvUploadValidationResponse | null = null;
  public selectedFile: File | null = null;
  public clientErrorMessage = '';
  public serverErrorMessage = '';
  public successMessage = '';

  private token: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private cvUploadService: CvUploadService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
    if (!this.token) {
      this.state = 'invalid';
      this.validation = {
        valid: false,
        candidateName: null,
        expiresAt: null,
        errorMessage: 'Nedostaje token u poveznici. Provjerite link iz e-maila.',
      };
      return;
    }
    this.cvUploadService
      .validateToken(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.validation = response;
          this.state = response.valid ? 'valid' : 'invalid';
        },
        error: () => {
          this.state = 'invalid';
          this.validation = {
            valid: false,
            candidateName: null,
            expiresAt: null,
            errorMessage: 'Greska pri provjeri linka. Pokusajte ponovno kasnije.',
          };
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    this.clientErrorMessage = '';
    this.selectedFile = null;
    if (!file) {
      return;
    }
    const error = this.validateFile(file);
    if (error) {
      this.clientErrorMessage = error;
      input.value = '';
      return;
    }
    this.selectedFile = file;
  }

  public onSubmit(): void {
    if (!this.token || !this.selectedFile || this.state === 'uploading') {
      return;
    }
    this.state = 'uploading';
    this.serverErrorMessage = '';
    this.cvUploadService
      .uploadCv(this.token, this.selectedFile)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.state = 'success';
            this.successMessage = response.message || 'Vas zivotopis je uspjesno uploadan.';
          } else {
            this.state = 'error';
            this.serverErrorMessage = response.message || 'Upload nije uspio. Pokusajte ponovno.';
          }
        },
        error: (err: HttpErrorResponse) => {
          this.state = 'error';
          this.serverErrorMessage =
            err?.error?.message || 'Upload nije uspio. Pokusajte ponovno.';
        },
      });
  }

  public retryUpload(): void {
    this.state = 'valid';
    this.serverErrorMessage = '';
  }

  public formatExpiresAt(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year}. ${hours}:${minutes}`;
  }

  public formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  private validateFile(file: File): string | null {
    if (file.size > MAX_SIZE_BYTES) {
      return 'Datoteka je prevelika. Maksimalno 10 MB.';
    }
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return 'Neispravan format. Dozvoljeno: PDF, DOC, DOCX.';
    }
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
      return 'Neispravan format. Dozvoljeno: PDF, DOC, DOCX.';
    }
    return null;
  }
}
